/**
 * Минимальный i18n для IFC Web Viewer.
 *
 * - Поддерживает `ru` и `en`.
 * - Текущий язык хранится в `localStorage` (`ifc-viewer.lang`).
 * - На элементах HTML используется три атрибута:
 *   - `data-i18n="<key>"`            — заменяет textContent на перевод;
 *   - `data-i18n-placeholder="<key>"` — заменяет атрибут placeholder;
 *   - `data-i18n-attr="attr:key,..."` — заменяет произвольные атрибуты
 *     (например, `title:tooltip.fit,aria-label:tooltip.fit`).
 *
 * Этап 5 — это «минимальный словарь»: переводятся статические подписи UI и
 * заголовки. Динамические сообщения статуса (`setStatus`) пока остаются на
 * русском — их перевод будет следующим инкрементом.
 */

export type Lang = "ru" | "en";

export const LANG_STORAGE_KEY = "ifc-viewer.lang";

type Dict = Record<string, string>;

const ru: Dict = {
  "app.title": "IFC Web Viewer - ИНЖПРОФФ",

  // ---- Side rail tooltips / aria ----
  // Боковое меню объединено: 12 разделов → 8 (см. README/PR-описание).
  // «Выделение» = «Выделение и примечания» + «Свойства выделения»;
  // «Поиск и фильтры» = «Фильтрация IFC-элементов» + «Поиск элементов»;
  // «Визуализация» = «Разрезы» + «Виды» + «Цветовые правила».
  "section.model": "Модель",
  "section.general": "Общие данные IFC",
  "section.selection": "Выделение",
  "section.measure": "Линейка и измерение",
  "section.filters": "Поиск и фильтры",
  "section.compare": "Сравнение ревизий",
  "section.tree": "Дерево пространственной структуры",
  "section.properties": "Свойства выделения",
  "section.clip": "Разрезы (clipping planes)",
  "section.views": "Виды (закладки камеры)",
  "section.search": "Поиск элементов",
  "section.color": "Цветовые правила",
  "section.visual": "Визуализация",
  "section.settings": "Настройки",
  "section.sidemenu": "Разделы меню",
  "sidebar.toggleHint": "Свернуть или развернуть меню",
  "sidebar.openIfcPanel": "Открыть панель IFC",

  // ---- Объединённые панели (заголовки групп) и их подвкладки ----
  "panel.selection.head": "Выделение",
  "panel.searchFilter.head": "Поиск и фильтры",
  "panel.visual.head": "Визуализация",
  "subtab.selectionInfo": "Инфо",
  "subtab.selectionProps": "Свойства",
  "subtab.search": "Поиск",
  "subtab.filter": "Фильтр",
  "subtab.clip": "Разрезы",
  "subtab.views": "Виды",
  "subtab.color": "Цвета",
  "subtab.plan": "План",

  // ---- Section 1 ----
  "section1.head": "Общие данные IFC",
  "section1.intro":
    "Откройте IFC через кнопку + на панели вкладок (с ПК или из презентационных файлов). ЛКМ — орбита, ПКМ — панорама, колесо — зум. Зажмите Ctrl (или включите «Мультивыбор») и кликайте/тапайте по элементам, чтобы собрать группу выделения (повторный клик снимает элемент).",
  "btn.fitCamera": "По центру",
  "btn.wireframe": "X-Ray",
  "btn.wireframeOff": "Solid",
  "btn.clearScene": "Очистить",
  "btn.multiselect": "Мультивыбор: выкл",
  "btn.multiselectOn": "Мультивыбор: вкл",
  "btn.multiselect.tooltip":
    "Пока включено, каждый клик/тап по элементу добавляет его к выделению вместо замены — замена Ctrl+клик на устройствах без клавиатуры.",
  "status.multiselectOn":
    "Режим мультивыбора включён: клик/тап добавляет элемент к текущему выделению.",
  "status.multiselectOff": "Режим мультивыбора выключен.",
  "status.ready": "Готово к загрузке IFC.",
  "info.empty": "Файл не загружен.",
  "btn.shareLink": "Скопировать share-link",
  "btn.shareLink.tooltip": "Скопировать ссылку на текущий ракурс",

  // ---- Section 2 ----
  "section2.head": "Выделение и примечания",
  "btn.hideSelected": "Скрыть выделенное",
  "btn.isolateSelected": "Изолировать выделенное",
  "btn.showAllHidden": "Показать скрытые",
  "btn.screenshot": "Снимок canvas",
  "selection.none": "Выделение: нет",
  "selection.one": "Выделение ({count}):",
  "selection.id": "ID",
  "selection.type": "Тип",
  "selection.name": "Наименование",
  "selection.adsk": "ADSK_Наименование",
  "selection.many": "Выделено элементов: {count}",
  "selection.ids": "Express ID",
  "selection.more": "… и ещё {count}",
  "btn.addAnnotation": "Примечание к выделению",
  "btn.openAnnotationsList": "Все примечания",
  "section2.hint":
    "«Скрыть выделенное» убирает выделенные элементы (остаются полупрозрачным «призраком»). «Изолировать» скрывает всё, кроме выделения. «Показать скрытые» возвращает всё в обычный вид. «Снимок canvas» сохраняет PNG-картинку текущей сцены.",

  // ---- Section 3 ----
  "section3.head": "Линейка и измерение",
  "btn.measureOff": "Линейка: выкл",
  "btn.measureOn": "Линейка: вкл",
  "btn.clearMeasure": "Очистить замеры",
  "measure.info.off": "Линейка: выключена.",
  "measure.info.loadIfc": "Линейка: загрузите IFC модель.",
  "measure.info.pickFirst": "Линейка: выберите первую точку.",
  "measure.info.pickSecond": "Линейка: выберите вторую точку.",
  "measure.unitLabel": "Единицы измерения:",
  "measure.snapLabel": "Снэп линейки:",
  "measure.history.empty": "История замеров пуста.",
  "measure.history.delete": "Удалить",
  "measure.unit.cm": "см",
  "measure.unit.mm": "мм",
  "measure.snap.surface": "поверхность",
  "measure.snap.vertex": "вершина",
  "measure.snap.edge": "ребро",
  "measure.snap.center": "центр грани",

  // ---- Section 4 ----
  "section4.head": "Фильтрация IFC-элементов",
  "filter.storey": "Этаж",
  "filter.allStoreys": "Все этажи",
  "filter.type": "Тип элемента",
  "filter.allTypes": "Все типы",
  "filter.type.tooltip": "Удерживайте Ctrl для выбора нескольких типов",
  "filter.mark": "Марка (Tag/Name)",
  "filter.markPlaceholder": "Например: АР-01",
  "filter.hint":
    "Управляет видимостью элементов по этажу, типу IFC и марке (Tag/Name). Задайте условия и нажмите «Применить фильтр»; «Сбросить» возвращает все элементы.",
  "btn.applyFilters": "Применить фильтр",
  "btn.clearFilters": "Сбросить",

  // ---- Section 5 ----
  "section5.head": "Сравнение IFC-ревизий (по GUID)",
  "compare.baselineLabel": "Базовая IFC-ревизия для сравнения",
  "compare.currentLabel": "Текущая модель",
  "compare.noModel": "Модель не загружена",
  "compare.baselineLabel2": "Базовая ревизия",
  "compare.noFile": "Файл не выбран",
  "compare.loadFromPc": "Загрузить IFC ревизию",
  "compare.loadFromYandex": "Загрузка из Яндекс Диска",
  "compare.fromCde": "Версия из истории СОД",
  "compare.cdeRefresh": "Обновить список версий",
  "compare.cdeOpen": "Сравнить с выбранной версией",
  "compare.cdeLoading": "Загрузка списка версий…",
  "compare.run": "Сравнить",
  "compare.clear": "Сбросить сравнение",
  "compare.showRemoved": "Показывать удалённые красным",
  "compare.currentHint":
    "Текущая модель — активная (обновлённая) версия, открытая в 3D. Считается более новой; на ней показываются изменения.",
  "compare.baselineHint":
    "Базовая ревизия — прошлая версия для сравнения. С ней сопоставляется текущая модель по GUID (GlobalId).",
  "compare.legendTitle": "Цветовая маркировка",
  "compare.legendAdded": "Добавлено",
  "compare.legendChanged": "Изменено",
  "compare.legendRemoved": "Удалено",
  "compare.addedHint":
    "Зелёный — элементы, которые есть в текущей модели, но отсутствуют в базовой ревизии (добавлены).",
  "compare.changedHint":
    "Оранжевый — элементы с тем же GUID, но изменённой геометрией или свойствами. Подсвечиваются на текущей модели.",
  "compare.removedHint":
    "Красный — элементы, которые есть только в базовой ревизии (удалены). Включается галочкой «Показывать удалённые красным».",
  "compare.notRun": "Сравнение не выполнено.",

  // ---- Section 6 (settings) ----
  "section6.head": "Настройки",
  "settings.language": "Язык интерфейса",
  "settings.lang.ru": "Русский",
  "settings.lang.en": "English",
  "settings.theme": "Оформление",
  "settings.theme.auto": "Авто",
  "settings.theme.dark": "Тёмная",
  "settings.theme.light": "Светлая",
  "settings.grid": "Показывать координатную сетку",
  "settings.pwa": "Установить как приложение",
  "settings.pwa.btn": "Установить",
  "settings.pwa.unavailable":
    "Установка как приложения недоступна в этом браузере.",

  // ---- Section 7 (tree) ----
  "section7.head": "Дерево пространственной структуры",
  "tree.hint":
    "Project → Site → Building → Storey → Element. Чекбокс — видимость, двойной клик — фокус по выделению.",
  "tree.expandAll": "Развернуть всё",
  "tree.collapseAll": "Свернуть всё",
  "tree.empty": "Загрузите IFC для просмотра дерева.",
  "tree.ariaLabel": "Дерево IFC",

  // ---- Section 8 (properties) ----
  "section8.head": "Свойства выделения",
  "props.hint":
    "Все PropertySets, Quantities, материалы и базовые поля выделенного элемента.",
  "props.empty": "Выделите элемент в сцене.",

  // ---- Section 9 (clip) ----
  "section9.head": "Разрезы (clipping)",
  "clip.hint":
    "Глобальные плоскости X, Y, Z. Ползунок — положение в долях от габарита модели.",
  "clip.enable": "Включить разрезы",
  "clip.axisX": "Ось X",
  "clip.axisY": "Ось Y",
  "clip.axisZ": "Ось Z",
  "clip.reset": "Сбросить срезы",
  "clip.lockedByPlan": "Разрезы недоступны, пока открыт план.",

  "plan.head": "План этажа",
  "plan.hint":
    "Вид сверху на выбранный этаж. Видны только элементы, которые проходят фильтры из раздела «Поиск и фильтры». Разрез проходит сразу под плитой следующего этажа, поэтому элементы под потолком остаются.",
  "plan.open": "Открыть план",
  "plan.close": "Вернуться в 3D",
  "plan.needModel": "Загрузите IFC, чтобы открыть план.",
  "plan.noStoreys": "В модели нет этажей IfcBuildingStorey.",
  "plan.idle": "План выключен. Откройте его, чтобы смотреть этаж сверху.",
  "plan.status": "Этаж: {name}. Показаны элементы по текущим фильтрам.",
  "plan.emptyFilter": "По текущим фильтрам на этом этаже ничего не видно.",
  "plan.wholeModel": "вся модель",

  // ---- Section 10 (views) ----
  "section10.head": "Виды (закладки камеры)",
  "views.hint":
    "Сохраняет позицию камеры, видимость, фильтры, срезы и цветовое правило для текущего IFC.",
  "views.namePlaceholder": "Название вида…",
  "views.save": "Сохранить вид",
  "views.empty": "Видов пока нет.",
  "views.planBadge": "План",

  // ---- Section 11 (search) ----
  "section11.head": "Поиск элементов",
  "search.hint":
    "По GlobalId (IFC GUID), Name, Tag/марке или IFC-типу. Введите запрос и нажмите Enter.",
  "search.placeholder": "Например: IFCWALL, АР-01, 2N9…",
  "search.run": "Найти",
  "search.status.idle": "Введите запрос для поиска.",

  // ---- Section 12 (color rules) ----
  "section12.head": "Цветовые правила",
  "color.hint":
    "Раскраска элементов по типу, этажу или марке. Создаёт стабильную палитру и легенду.",
  "color.modeLabel": "Режим",
  "color.mode.none": "Без раскраски",
  "color.mode.type": "По типу IFC",
  "color.mode.storey": "По этажу",
  "color.mode.mark": "По марке/Tag",
  "color.apply": "Применить",
  "color.clear": "Сбросить",
  "color.legend.empty": "Раскраска отключена.",

  // ---- IFC tabs ----
  "tabs.ariaLabel": "IFC вкладки",
  "ifcAdd.fromPc": "Загрузить IFC",
  "ifcAdd.presentationTitle": "Презентационные файлы",
  "ifcAdd.compareBaseline":
    "Загрузить выбранный IFC как базовую ревизию",

  // ---- Modals ----
  "modal.annotation.title.new": "Новое примечание",
  "modal.annotation.title.edit": "Редактирование примечания",
  "modal.annotation.field.title": "Название",
  "modal.annotation.placeholder.title": "Краткий заголовок",
  "modal.annotation.field.priority": "Приоритет",
  "modal.annotation.priority.suspended": "Приостановлено",
  "modal.annotation.priority.low": "Низкий",
  "modal.annotation.priority.normal": "Обычный",
  "modal.annotation.priority.high": "Высокий",
  "modal.annotation.priority.critical": "Критичный",
  "modal.annotation.field.deadline": "Срок выполнения",
  "modal.annotation.field.description": "Описание",
  "modal.annotation.placeholder.description":
    "Текст и вставленные изображения…",
  "modal.annotation.cancel": "Отмена",
  "modal.annotation.save": "Сохранить",
  "modal.annotation.list.title": "Список примечаний",
  "modal.annotation.export.word": "Скачать Word (.doc)",
  "modal.annotation.export.bcf": "Экспорт BCF",
  "modal.annotation.export.bcf.tooltip": "Экспорт всех примечаний в BCF 2.1",
  "modal.annotation.import.bcf": "Импорт BCF…",
  "modal.annotation.import.bcf.tooltip": "Импорт примечаний из BCF 2.x",
  "modal.annotation.list.close": "Закрыть",

  // ---- Image menu ----
  "imageMenu.delete": "Удалить",

  // ---- Drop zone ----
  "dropZone.title": "Отпустите IFC, чтобы открыть",
  "dropZone.hint": ".ifc или .ifczip",
};

const en: Dict = {
  "app.title": "IFC Web Viewer - INZHPROFF",

  "section.model": "Model",
  "section.general": "IFC overview",
  "section.selection": "Selection",
  "section.measure": "Ruler & measurements",
  "section.filters": "Search & filters",
  "section.compare": "Revision compare",
  "section.tree": "Spatial structure tree",
  "section.properties": "Selection properties",
  "section.clip": "Clipping planes",
  "section.views": "Camera bookmarks",
  "section.search": "Element search",
  "section.color": "Color rules",
  "section.visual": "Visualization",
  "section.settings": "Settings",
  "section.sidemenu": "Side menu sections",
  "sidebar.toggleHint": "Collapse or expand the side menu",
  "sidebar.openIfcPanel": "Open IFC panel",

  "panel.selection.head": "Selection",
  "panel.searchFilter.head": "Search & filters",
  "panel.visual.head": "Visualization",
  "subtab.selectionInfo": "Info",
  "subtab.selectionProps": "Properties",
  "subtab.search": "Search",
  "subtab.filter": "Filter",
  "subtab.clip": "Clipping",
  "subtab.views": "Views",
  "subtab.color": "Colors",
  "subtab.plan": "Plan",

  "section1.head": "IFC overview",
  "section1.intro":
    "Open an IFC via the + button on the tab bar (from a PC or a presentation file). LMB — orbit, RMB — pan, wheel — zoom. Hold Ctrl (or enable “Multi-select”) and click/tap elements to build a multi-selection (clicking again removes an element).",
  "btn.fitCamera": "Fit camera",
  "btn.wireframe": "X-Ray",
  "btn.wireframeOff": "Solid",
  "btn.clearScene": "Clear",
  "btn.multiselect": "Multi-select: off",
  "btn.multiselectOn": "Multi-select: on",
  "btn.multiselect.tooltip":
    "While on, every click/tap on an element adds it to the selection instead of replacing it — replaces Ctrl+click on devices without a keyboard.",
  "status.multiselectOn":
    "Multi-select mode on: click/tap adds the element to the current selection.",
  "status.multiselectOff": "Multi-select mode off.",
  "status.ready": "Ready to load an IFC.",
  "info.empty": "No file loaded.",
  "btn.shareLink": "Copy share-link",
  "btn.shareLink.tooltip": "Copy a link to the current view",

  "section2.head": "Selection & notes",
  "btn.hideSelected": "Hide selected",
  "btn.isolateSelected": "Isolate selected",
  "btn.showAllHidden": "Show hidden",
  "btn.screenshot": "Canvas snapshot",
  "selection.none": "Selection: none",
  "selection.one": "Selection ({count}):",
  "selection.id": "ID",
  "selection.type": "Type",
  "selection.name": "Name",
  "selection.adsk": "ADSK_Наименование",
  "selection.many": "Selected elements: {count}",
  "selection.ids": "Express ID",
  "selection.more": "… and {count} more",
  "btn.addAnnotation": "Note for selection",
  "btn.openAnnotationsList": "All notes",
  "section2.hint":
    "Hide selected removes selected elements (they remain as a semi-transparent ghost). Isolate hides everything except the selection. Show hidden restores normal visibility. Canvas snapshot saves a PNG of the current scene.",

  "section3.head": "Ruler & measurements",
  "btn.measureOff": "Ruler: off",
  "btn.measureOn": "Ruler: on",
  "btn.clearMeasure": "Clear measurements",
  "measure.info.off": "Ruler: off.",
  "measure.info.loadIfc": "Ruler: load an IFC model first.",
  "measure.info.pickFirst": "Ruler: pick the first point.",
  "measure.info.pickSecond": "Ruler: pick the second point.",
  "measure.unitLabel": "Units:",
  "measure.snapLabel": "Snap mode:",
  "measure.history.empty": "Measurement history is empty.",
  "measure.history.delete": "Delete",
  "measure.unit.cm": "cm",
  "measure.unit.mm": "mm",
  "measure.snap.surface": "surface",
  "measure.snap.vertex": "vertex",
  "measure.snap.edge": "edge",
  "measure.snap.center": "face center",

  "section4.head": "IFC element filters",
  "filter.storey": "Storey",
  "filter.allStoreys": "All storeys",
  "filter.type": "Element type",
  "filter.allTypes": "All types",
  "filter.type.tooltip": "Hold Ctrl to select multiple types",
  "filter.mark": "Mark (Tag/Name)",
  "filter.markPlaceholder": "e.g. AR-01",
  "filter.hint":
    "Controls element visibility by storey, IFC type and mark (Tag/Name). Set the conditions and click Apply filter; Reset restores all elements.",
  "btn.applyFilters": "Apply filter",
  "btn.clearFilters": "Reset",

  "section5.head": "IFC revision compare (by GUID)",
  "compare.baselineLabel": "Baseline IFC revision to compare against",
  "compare.currentLabel": "Current model",
  "compare.noModel": "No model loaded",
  "compare.baselineLabel2": "Baseline revision",
  "compare.noFile": "No file selected",
  "compare.loadFromPc": "Load IFC revision",
  "compare.loadFromYandex": "Load from Yandex Disk",
  "compare.fromCde": "Version from CDE history",
  "compare.cdeRefresh": "Refresh version list",
  "compare.cdeOpen": "Compare with selected version",
  "compare.cdeLoading": "Loading version list…",
  "compare.run": "Compare",
  "compare.clear": "Reset compare",
  "compare.showRemoved": "Show removed in red",
  "compare.currentHint":
    "Current model — the active (updated) version open in 3D. Treated as the newer one; changes are highlighted on it.",
  "compare.baselineHint":
    "Baseline revision — the previous version to compare against. The current model is matched to it by GUID (GlobalId).",
  "compare.legendTitle": "Color legend",
  "compare.legendAdded": "Added",
  "compare.legendChanged": "Changed",
  "compare.legendRemoved": "Removed",
  "compare.addedHint":
    "Green — elements present in the current model but missing in the baseline revision (added).",
  "compare.changedHint":
    "Orange — elements with the same GUID but changed geometry or properties. Highlighted on the current model.",
  "compare.removedHint":
    "Red — elements present only in the baseline revision (removed). Enabled via the \"Show removed in red\" checkbox.",
  "compare.notRun": "Compare not run.",

  "section6.head": "Settings",
  "settings.language": "Interface language",
  "settings.lang.ru": "Russian",
  "settings.lang.en": "English",
  "settings.theme": "Theme",
  "settings.theme.auto": "Auto",
  "settings.theme.dark": "Dark",
  "settings.theme.light": "Light",
  "settings.grid": "Show coordinate grid",
  "settings.pwa": "Install as application",
  "settings.pwa.btn": "Install",
  "settings.pwa.unavailable":
    "App installation is not available in this browser.",

  "section7.head": "Spatial structure tree",
  "tree.hint":
    "Project → Site → Building → Storey → Element. Checkbox toggles visibility, double-click focuses the selection.",
  "tree.expandAll": "Expand all",
  "tree.collapseAll": "Collapse all",
  "tree.empty": "Load an IFC to see the tree.",
  "tree.ariaLabel": "IFC tree",

  "section8.head": "Selection properties",
  "props.hint":
    "All PropertySets, Quantities, materials and base fields of the selected element.",
  "props.empty": "Select an element in the scene.",

  "section9.head": "Clipping",
  "clip.hint":
    "Global X / Y / Z planes. Slider — position as a fraction of the model bounding box.",
  "clip.enable": "Enable clipping",
  "clip.axisX": "X axis",
  "clip.axisY": "Y axis",
  "clip.axisZ": "Z axis",
  "clip.reset": "Reset cuts",
  "clip.lockedByPlan": "Clipping is unavailable while the plan is open.",

  "plan.head": "Floor plan",
  "plan.hint":
    "Top view of the selected storey. Only elements that pass Search and filters are shown. The cut sits just under the next storey slab, so ceiling-mounted elements stay visible.",
  "plan.open": "Open plan",
  "plan.close": "Back to 3D",
  "plan.needModel": "Load an IFC to open the plan.",
  "plan.noStoreys": "This model has no IfcBuildingStorey levels.",
  "plan.idle": "Plan is off. Open it to look down on a storey.",
  "plan.status": "Storey: {name}. Showing elements that pass the current filters.",
  "plan.emptyFilter": "Nothing on this storey matches the current filters.",
  "plan.wholeModel": "whole model",

  "section10.head": "Camera bookmarks",
  "views.hint":
    "Saves camera position, visibility, filters, cuts and color rule for the current IFC.",
  "views.namePlaceholder": "View name…",
  "views.save": "Save view",
  "views.empty": "No views yet.",
  "views.planBadge": "Plan",

  "section11.head": "Element search",
  "search.hint":
    "By GlobalId (IFC GUID), Name, Tag/mark or IFC type. Type a query and press Enter.",
  "search.placeholder": "e.g. IFCWALL, AR-01, 2N9…",
  "search.run": "Search",
  "search.status.idle": "Enter a query to search.",

  "section12.head": "Color rules",
  "color.hint":
    "Color elements by type, storey or mark. Builds a stable palette and a legend.",
  "color.modeLabel": "Mode",
  "color.mode.none": "No coloring",
  "color.mode.type": "By IFC type",
  "color.mode.storey": "By storey",
  "color.mode.mark": "By mark / tag",
  "color.apply": "Apply",
  "color.clear": "Reset",
  "color.legend.empty": "Coloring disabled.",

  "tabs.ariaLabel": "IFC tabs",
  "ifcAdd.fromPc": "Load IFC",
  "ifcAdd.presentationTitle": "Presentation files",
  "ifcAdd.compareBaseline": "Load selected IFC as the baseline revision",

  "modal.annotation.title.new": "New note",
  "modal.annotation.title.edit": "Edit note",
  "modal.annotation.field.title": "Title",
  "modal.annotation.placeholder.title": "Short title",
  "modal.annotation.field.priority": "Priority",
  "modal.annotation.priority.suspended": "Suspended",
  "modal.annotation.priority.low": "Low",
  "modal.annotation.priority.normal": "Normal",
  "modal.annotation.priority.high": "High",
  "modal.annotation.priority.critical": "Critical",
  "modal.annotation.field.deadline": "Deadline",
  "modal.annotation.field.description": "Description",
  "modal.annotation.placeholder.description":
    "Text and pasted images…",
  "modal.annotation.cancel": "Cancel",
  "modal.annotation.save": "Save",
  "modal.annotation.list.title": "Notes list",
  "modal.annotation.export.word": "Download Word (.doc)",
  "modal.annotation.export.bcf": "Export BCF",
  "modal.annotation.export.bcf.tooltip": "Export all notes as BCF 2.1",
  "modal.annotation.import.bcf": "Import BCF…",
  "modal.annotation.import.bcf.tooltip": "Import notes from BCF 2.x",
  "modal.annotation.list.close": "Close",

  "imageMenu.delete": "Delete",

  "dropZone.title": "Drop an IFC to open",
  "dropZone.hint": ".ifc or .ifczip",
};

const DICTIONARIES: Record<Lang, Dict> = { ru, en };

let currentLang: Lang = "ru";
const subscribers = new Set<(lang: Lang) => void>();

function detectInitialLang(): Lang {
  const saved = localStorage.getItem(LANG_STORAGE_KEY);
  if (saved === "ru" || saved === "en") {
    return saved;
  }
  const browser = (navigator.language || "ru").slice(0, 2).toLowerCase();
  return browser === "en" ? "en" : "ru";
}

export function getLang(): Lang {
  return currentLang;
}

/**
 * Человекочитаемые русские названия IFC-классов. Значение в фильтрах/правилах
 * остаётся исходным кодом ("IFCDOOR"), локализуется только отображаемый текст.
 * Для английского языка показываем сам код как есть.
 */
const IFC_TYPE_RU: Record<string, string> = {
  IFCWALL: "Стена",
  IFCWALLSTANDARDCASE: "Стена (типовая)",
  IFCCURTAINWALL: "Витраж (навесная стена)",
  IFCSLAB: "Плита/перекрытие",
  IFCROOF: "Кровля",
  IFCBEAM: "Балка",
  IFCCOLUMN: "Колонна",
  IFCMEMBER: "Элемент каркаса",
  IFCPLATE: "Панель (plate)",
  IFCFOOTING: "Фундамент",
  IFCPILE: "Свая",
  IFCDOOR: "Дверь",
  IFCWINDOW: "Окно",
  IFCSTAIR: "Лестница",
  IFCSTAIRFLIGHT: "Лестничный марш",
  IFCRAMP: "Пандус",
  IFCRAMPFLIGHT: "Марш пандуса",
  IFCRAILING: "Ограждение/перила",
  IFCCOVERING: "Отделка/облицовка",
  IFCSPACE: "Помещение",
  IFCOPENINGELEMENT: "Проём",
  IFCBUILDINGELEMENTPROXY: "Обобщённый элемент",
  IFCFURNISHINGELEMENT: "Мебель/оборудование",
  IFCFURNITURE: "Мебель",
  IFCSYSTEMFURNITUREELEMENT: "Мебельная система",
  IFCFLOWTERMINAL: "Оконечное устройство",
  IFCFLOWSEGMENT: "Сегмент сети",
  IFCFLOWFITTING: "Фитинг",
  IFCFLOWCONTROLLER: "Регулирующая арматура",
  IFCFLOWMOVINGDEVICE: "Насос/вентилятор",
  IFCFLOWSTORAGEDEVICE: "Накопитель (бак/резервуар)",
  IFCFLOWTREATMENTDEVICE: "Устройство очистки",
  IFCFLOWINSTRUMENT: "КИП (датчик/прибор)",
  IFCENERGYCONVERSIONDEVICE: "Энергопреобразователь",
  IFCPIPESEGMENT: "Сегмент трубы",
  IFCPIPEFITTING: "Фитинг трубы",
  IFCDUCTSEGMENT: "Сегмент воздуховода",
  IFCDUCTFITTING: "Фитинг воздуховода",
  IFCLIGHTFIXTURE: "Светильник",
  IFCSANITARYTERMINAL: "Сантехприбор",
  IFCDISTRIBUTIONELEMENT: "Инж. элемент",
  IFCDISTRIBUTIONFLOWELEMENT: "Инж. элемент (поток)",
  IFCDISTRIBUTIONCONTROLELEMENT: "Элемент КИПиА",
  IFCDISTRIBUTIONPORT: "Инж. порт",
  IFCREINFORCINGBAR: "Арматурный стержень",
  IFCREINFORCINGMESH: "Арматурная сетка",
  IFCTENDON: "Напрягаемый канат",
  IFCFASTENER: "Крепёж",
  IFCMECHANICALFASTENER: "Метизный крепёж",
  IFCDISCRETEACCESSORY: "Аксессуар/закладная",
  IFCBUILDINGELEMENTPART: "Часть элемента",
  IFCTRANSPORTELEMENT: "Транспортный элемент (лифт/эскалатор)",
  IFCSHADINGDEVICE: "Солнцезащита",
  IFCCHIMNEY: "Дымоход",
  IFCBUILDINGSTOREY: "Этаж",
  IFCBUILDING: "Здание",
  IFCSITE: "Площадка",
  IFCPROJECT: "Проект",
  IFCANNOTATION: "Аннотация",
  IFCWALLELEMENTEDCASE: "Стена (составная)",
};

/**
 * Локализованная подпись IFC-типа для отображения в UI.
 * RU: "Дверь" (или сам код, если нет в словаре). EN: исходный код "IFCDOOR".
 */
export function ifcTypeLabel(type: string): string {
  if (!type) {
    return "";
  }
  if (currentLang === "ru") {
    return IFC_TYPE_RU[type.toUpperCase()] || type;
  }
  return type;
}

export function t(key: string, params?: Record<string, string | number>): string {
  const value = DICTIONARIES[currentLang]?.[key] ?? DICTIONARIES.ru[key] ?? key;
  if (!params) {
    return value;
  }
  let result = value;
  for (const [k, v] of Object.entries(params)) {
    result = result.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
  }
  return result;
}

export function setLang(lang: Lang): void {
  if (lang !== "ru" && lang !== "en") {
    return;
  }
  if (lang === currentLang) {
    return;
  }
  currentLang = lang;
  localStorage.setItem(LANG_STORAGE_KEY, lang);
  document.documentElement.lang = lang;
  applyI18nToDom();
  subscribers.forEach((cb) => {
    try {
      cb(lang);
    } catch (_) {
      /* ignore */
    }
  });
}

export function onLangChange(cb: (lang: Lang) => void): void {
  subscribers.add(cb);
}

function applyToElement(el: HTMLElement): void {
  const textKey = el.dataset.i18n;
  if (textKey) {
    el.textContent = t(textKey);
  }
  const placeholderKey = el.dataset.i18nPlaceholder;
  if (placeholderKey && "placeholder" in el) {
    (el as HTMLInputElement | HTMLTextAreaElement).placeholder = t(placeholderKey);
  }
  const attrSpec = el.dataset.i18nAttr;
  if (attrSpec) {
    const pairs = attrSpec.split(",");
    for (const pair of pairs) {
      const idx = pair.indexOf(":");
      if (idx <= 0) {
        continue;
      }
      const attr = pair.slice(0, idx).trim();
      const key = pair.slice(idx + 1).trim();
      if (!attr || !key) {
        continue;
      }
      el.setAttribute(attr, t(key));
    }
  }
}

export function applyI18nToDom(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-i18n]").forEach(applyToElement);
  root
    .querySelectorAll<HTMLElement>("[data-i18n-placeholder]")
    .forEach(applyToElement);
  root.querySelectorAll<HTMLElement>("[data-i18n-attr]").forEach(applyToElement);
}

export function initI18n(): void {
  currentLang = detectInitialLang();
  document.documentElement.lang = currentLang;
  applyI18nToDom();
}
